'use client'

import { cn } from '@/lib/tailwind'
import {
  CheckIcon,
  CircleNotchIcon,
  CaretDownIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react'
import type { MediaKind } from '@/entries'
import { ENTRY_PRESENTATION } from '@/entries'

interface ActionBarProps {
  type: MediaKind
  confidence: 'high' | 'low' | 'medium' | 'none' | undefined
  hasAlternatives: boolean
  alternativesCount: number
  showAlternatives: boolean
  onToggleAlternatives: () => void
  onEdit?: () => void
  onDismiss: () => void
  onConfirm: () => void
  isSubmitting?: boolean
  disabled?: boolean
}

/**
 * Action bar for media preview with confirm/dismiss/edit actions
 */
export function ActionBar({
  type,
  confidence,
  hasAlternatives,
  alternativesCount,
  showAlternatives,
  onToggleAlternatives,
  onEdit,
  onDismiss,
  onConfirm,
  isSubmitting = false,
  disabled = false,
}: ActionBarProps) {
  const colors = ENTRY_PRESENTATION[type]

  return (
    <div className="bg-muted/50 flex items-center justify-between border-t px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-xs">
          {confidence === 'high' ? 'Match found' : 'Possible match'}
        </span>
        {hasAlternatives && (
          <button
            type="button"
            onClick={onToggleAlternatives}
            className="text-muted-foreground hover:text-foreground flex items-center gap-0.5 text-xs transition-colors"
          >
            <CaretDownIcon
              className={cn(
                'h-3 w-3 transition-transform',
                showAlternatives && 'rotate-180',
              )}
            />
            {showAlternatives ? 'Hide' : `${alternativesCount} more`}
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            disabled={disabled}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors disabled:opacity-50"
            title="Edit details"
          >
            <PencilSimpleIcon className="h-3 w-3" />
            Edit
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          disabled={disabled}
          className="text-muted-foreground hover:text-foreground text-xs transition-colors disabled:opacity-50"
        >
          Not this one
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={disabled}
          className={cn(
            'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            colors.combined,
            'hover:opacity-90 disabled:opacity-50',
          )}
        >
          {isSubmitting ? (
            <>
              <CircleNotchIcon className="h-3 w-3 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="h-3 w-3" />
              Add to Library
            </>
          )}
        </button>
      </div>
    </div>
  )
}
